import React from 'react';
import LegalPageLayout from '../components/LegalPageLayout';

const openCookiePreferences = () => {
  // Dispatch a global event picked up by CookieConsent.jsx
  window.dispatchEvent(new CustomEvent('open-cookie-preferences'));
};

const CookiePolicy = () => (
  <LegalPageLayout
    eyebrow="Legal · Cookies"
    title="Cookie Policy"
    lead="How eCOMM Merchant Solutions uses cookies on this website and how you can manage your preferences."
    testId="cookie-policy-page"
    topRightSlot={
      <button
        type="button"
        onClick={openCookiePreferences}
        className="inline-flex items-center gap-2 bg-white text-[#0a1929] hover:bg-white/90 font-semibold px-5 py-2.5 rounded-full text-sm transition-colors"
        data-testid="cookie-policy-manage-btn"
      >
        Manage Cookies
      </button>
    }
  >
    <h2>Definition and Purpose of Cookies</h2>
    <p>
      A cookie is a small file of letters and numbers that website operators store on your
      computer or mobile device. Our site uses cookies to distinguish you from other users of
      our site. They are widely used in order to make websites work, or work more efficiently,
      as well as to provide information to the owners of the site.
    </p>
    <p>
      This helps us to provide you with a good experience when you browse our site and also
      allows us to improve our site. We have implemented a cookie consent preferences manager on
      our site which allows you control over the non-essential cookies which our website sets.
      By using this consent manager — e.g. when you first browse our site — you can let us know
      whether you give your consent (or not) to the setting of the different types of cookies
      our website uses. You can also return to that tool to change your preferences at any time
      by clicking the <strong>"Manage Cookies"</strong> link located in the footer navigation on
      our website.
    </p>
    <p>
      Your cookie consent choices, on the device you consented, will be enforced by our cookie
      consent manager for a period of one year from the time you consent or from the time you
      make any change to your consent. After that one year period has expired without subsequent
      preference change by you, our system will reset and clear your preferences and request your
      consent again.
    </p>
    <p>
      Further information on cookies can be found on the website of the{' '}
      <a href="https://wikis.ec.europa.eu/display/EUROSTATHELP/How+to+manage+cookies" target="_blank" rel="noreferrer">European Commission</a>.
    </p>

    <h2>'Strictly Necessary' Cookies</h2>
    <p>
      These cookies are essential to enable you to move around our website and use its features,
      such as accessing secure areas of the website. Without these cookies, services you have
      asked for (e.g. an online application) cannot be provided.
    </p>

    <h2>Functional Cookies</h2>
    <p>
      These are cookies that allow our website to remember choices you make and to provide
      enhanced, more personal features. These cookies may be used to provide services you have
      asked for, such as watching a video, sharing website content on social media platforms and
      other third-party features.
    </p>
    <p>
      They are also used to understand and analyse key performance metrics of the website which
      helps in delivering a better user experience for our visitors. This information is used to
      improve how our website works including how we best provide services you have asked for,
      such as watching a video. These cookies collect data that is mostly aggregated and
      anonymous.
    </p>

    <h2>Analytics Cookies</h2>
    <p>
      Analytical cookies allow us to recognise and to count the number of visitors to our
      website, to see how visitors move around the website when they are using it and to
      understand which content visitors view and are interested in. This helps us study how
      frequently particular pages and areas of the website are visited and to determine the most
      popular areas of our website. It also enables us to improve the services which we offer to
      you by making sure that our users are finding the information that they are looking for.
      It also allows us to provide anonymised demographic data to third parties for appropriate
      advertising and to track the success of advertising campaigns in bringing prospective
      customers to our website.
    </p>

    <h2>Advertising Cookies</h2>
    <p>
      Advertising cookies are used to monitor the effectiveness of our online advertising
      campaigns. For example, for measuring how many unique browsers have seen a particular
      advertisement and how many of those subsequently visit our website. No individual users
      can be identified by the data these third-party cookies collect.
    </p>

    <h2>How to Manage Cookies</h2>

    <h3>Onsite Management</h3>
    <p>
      After the initial full or partial consent is given, you may manage or change your cookie
      preferences at any time during subsequent visits to ecomm365.com. To make a change, click
      the <button type="button" onClick={openCookiePreferences} className="text-[#1F4E79] underline underline-offset-2 hover:text-[#ef5b25]" data-testid="cookie-policy-inline-manage">'Manage Cookies'</button> link in the footer navigation. The preferences window also
      contains tabular listings of cookies used, by category, on the website.
    </p>

    <h3>Browser Management</h3>
    <p>
      All leading browsers have user controls available for managing and removing cookies. Below
      we provide links to browser-specific instructions on how you can do this. Generally, your
      browser will offer you the choice to accept, refuse or delete cookies at all times, or
      those from providers that website owners use ("third party cookies"), or those from
      specific websites.
    </p>
    <ul>
      <li><a href="https://support.google.com/accounts/answer/61416" target="_blank" rel="noreferrer">Google Chrome</a></li>
      <li><a href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences" target="_blank" rel="noreferrer">Firefox</a></li>
      <li><a href="https://support.apple.com/en-ie/guide/safari/sfri11471/mac" target="_blank" rel="noreferrer">Safari</a></li>
      <li><a href="https://support.apple.com/en-us/HT201265" target="_blank" rel="noreferrer">Mobile Safari</a></li>
      <li><a href="https://privacy.microsoft.com/en-us/windows-10-microsoft-edge-and-privacy" target="_blank" rel="noreferrer">Microsoft Edge</a></li>
      <li><a href="https://support.microsoft.com/en-ie/help/17442/windows-internet-explorer-delete-manage-cookies" target="_blank" rel="noreferrer">Internet Explorer</a></li>
    </ul>

    <h3>External Information</h3>
    <p>
      To find out more about cookies, including how to see what cookies have been set, visit{' '}
      <a href="https://www.aboutcookies.org/" target="_blank" rel="noreferrer">www.aboutcookies.org</a> or{' '}
      <a href="https://www.allaboutcookies.org/" target="_blank" rel="noreferrer">www.allaboutcookies.org</a>.
    </p>
    <p>To find information relating to other browsers, visit the browser developer's website.</p>
    <p>
      To opt out of being tracked by Google Analytics across all websites, visit{' '}
      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noreferrer">https://tools.google.com/dlpage/gaoptout</a>.
    </p>
    <p>
      If you have any queries on data protection, our Data Protection Officer may be contacted
      at: <a href="mailto:dpo@ecomm365.com">dpo@ecomm365.com</a>.
    </p>
  </LegalPageLayout>
);

export default CookiePolicy;
